import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineShieldCheck,
  HiOutlineChartBar,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineArrowTrendingUp,
  HiOutlineArrowTrendingDown,
  HiOutlineBuildingOffice2,
} from 'react-icons/hi2';
import { authService, plantService } from '../services';
import safetyObservationService from '../services/safetyObservationService';
import { usePermissions } from '../hooks/usePermissions';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import PageLoading from "../components/common/PageLoading";
import './SafetyObservationDashboard.css';

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedSafetyDashboardData = () => {
  try {
    const cachedPlants = sessionStorage.getItem('plants_data');
    return {
      plants: cachedPlants ? JSON.parse(cachedPlants) : [],
      hasPlants: !!cachedPlants
    };
  } catch (e) {
    return { plants: [], hasPlants: false };
  }
};

const SafetyObservationDashboard = () => {
  const navigate = useNavigate();
  const user = authService.getUser();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission check
  const canView = hasPermission('SafetyObservationAnalytics.View');

  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedSafetyDashboardData();
  // Check if we have cached plants - if yes, don't show loading initially
  const hasCachedPlantsOnMount = !!sessionStorage.getItem('plants_data');
  const [loading, setLoading] = useState(false); // Start with false to show page immediately
  const [error, setError] = useState('');
  const [trends, setTrends] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [roundsWithItems, setRoundsWithItems] = useState([]);
  const [plants, setPlants] = useState(cachedData.plants);
  const [selectedPlantId, setSelectedPlantId] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 months ago
    endDate: new Date().toISOString().split('T')[0], // today
  });

  // Refresh cached plants on mount
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedSafetyDashboardData();
      if (refreshedCachedData.hasPlants) {
        setPlants(refreshedCachedData.plants);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && canView) {
      const hasCachedPlants = sessionStorage.getItem('plants_data');
      if (!hasCachedPlants) {
        loadPlants();
      }
    }
  }, [tenantId, canView]);

  useEffect(() => {
    if (!tenantId) {
      setError('Please login to view Safety Observation analytics');
      setLoading(false);
      return;
    }

    if (!canView) {
      setError('You do not have permission to view Safety Observation analytics');
      setLoading(false);
      return;
    }

    const hasCachedPlants = sessionStorage.getItem('plants_data');

    if (hasCachedPlants) {
      setLoading(false);
      loadDashboardData(false);
    }

    const hasCachedPlantsNow = sessionStorage.getItem('plants_data');
    if (!hasCachedPlantsNow) {
      setLoading(true);
    }
    if (!hasCachedPlants) {
      loadDashboardData(!hasCachedPlantsNow);
    } else if (hasCachedPlants && !trends && !statistics) {
      loadDashboardData(false);
    }
  }, [dateRange, selectedPlantId, tenantId, canView]);

  const handlePlantChange = (e) => {
    const val = e.target.value;
    setSelectedPlantId(val);
    if (val) {
        localStorage.setItem('defaultPlantId', val);
    }
  };

  const loadPlants = async () => {
    try {
      const plantsData = await plantService.getAllPlants();
      setPlants(plantsData || []);
      // Cache plants
      sessionStorage.setItem('plants_data', JSON.stringify(plantsData || []));

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
        setSelectedPlantId(plantsData[0].plantId.toString());
      }
    } catch (err) {
      console.error('Error loading plants:', err);
    }
  };

  const loadDashboardData = async (showLoading = true) => {
    try {
      // Only show loading spinner if explicitly requested
      if (showLoading) {
        setLoading(true);
      }
      setError('');

      const filterParams = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      if (selectedPlantId) {
        filterParams.plantId = parseInt(selectedPlantId, 10);
      }

      const [trendsData, statsData, roundsResponse] = await Promise.all([
        safetyObservationService.getTrends(filterParams),
        safetyObservationService.getStatistics(filterParams),
        safetyObservationService.getSafetyObservationRounds(filterParams),
      ]);

      setTrends(trendsData);
      setStatistics(statsData);

      // Handle paginated response or direct array
      const roundsData = roundsResponse?.data || roundsResponse || [];
      setRounds(Array.isArray(roundsData) ? roundsData : []);

      // Load items for recent rounds only (last 20) for time series - NON-BLOCKING
      if (roundsData && roundsData.length > 0) {
        const recentRounds = roundsData
          .sort(
            (a, b) => new Date(b.observationDate) - new Date(a.observationDate),
          )
          .slice(0, 20);

        // Don't await this - let it run in background to unblock UI
        Promise.all(
          recentRounds.map(async (round) => {
            try {
              const items = await safetyObservationService.getItemsByRound(
                round.roundId,
              );
              return { ...round, items: items || [] };
            } catch (err) {
              return { ...round, items: [] };
            }
          }),
        ).then((roundsWithItemsData) => {
          setRoundsWithItems(roundsWithItemsData);
        });
      } else {
        setRoundsWithItems([]);
      }
    } catch (err) {
      setError('Failed to load dashboard data. Please try again.');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    if (!trends) return null;
    const totalSafe = trends.safeActs + trends.safeConditions;
    const totalUnsafe = trends.unsafeActs + trends.unsafeConditions;
    const total = totalSafe + totalUnsafe;
    const safetyRatio = total > 0 ? ((totalSafe / total) * 100).toFixed(1) : 0;
    const totalUnsafeItems = trends.openUnsafeItems + trends.closedUnsafeItems;
    const closureRate = totalUnsafeItems > 0 ? ((trends.closedUnsafeItems / totalUnsafeItems) * 100).toFixed(1) : 0;

    return {
      safetyRatio: parseFloat(safetyRatio),
      closureRate: parseFloat(closureRate),
      totalSafe,
      totalUnsafe,
      total,
    };
  }, [trends]);

  // Generate time series data for line chart
  const timeSeriesData = useMemo(() => {
    const sourceRounds = roundsWithItems.length > 0 ? roundsWithItems : rounds;
    // Ensure sourceRounds is always an array
    const roundsArray = Array.isArray(sourceRounds) ? sourceRounds : [];
    if (roundsArray.length === 0) return [];

    // Group by date using ISO date string as key for proper sorting
    const grouped = {};
    roundsArray.forEach(round => {
      const dateObj = new Date(round.observationDate);
      const dateKey = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD for sorting
      const dateDisplay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateDisplay,
          dateKey: dateKey,
          safe: 0,
          unsafe: 0,
          total: 0
        };
      }

      if (round.items && round.items.length > 0) {
        round.items.forEach(item => {
          if (item.observationType === 'Safe Act' || item.observationType === 'Safe Condition') {
            grouped[dateKey].safe += 1;
          } else {
            grouped[dateKey].unsafe += 1;
          }
        });
      }
      grouped[dateKey].total += 1;
    });

    // Sort by date key (ISO format) and take last 14 days
    const sortedDates = Object.keys(grouped).sort().slice(-14);
    return sortedDates.map(dateKey => grouped[dateKey]);
  }, [roundsWithItems, rounds]);

  // Observation type distribution for pie chart
  const observationTypeData = useMemo(() => {
    if (!trends) return [];
    return [
      { name: 'Safe Acts', value: trends.safeActs, color: '#22c55e' },
      { name: 'Safe Conditions', value: trends.safeConditions, color: '#16a34a' },
      { name: 'Unsafe Acts', value: trends.unsafeActs, color: '#ef4444' },
      { name: 'Unsafe Conditions', value: trends.unsafeConditions, color: '#dc2626' },
    ].filter(item => item.value > 0);
  }, [trends]);

  // Category data for bar chart
  const categoryData = useMemo(() => {
    if (!statistics?.categoryWiseBreakdown) return [];
    return statistics.categoryWiseBreakdown
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
      .map(cat => ({
        name: cat.categoryName?.length > 20 ? cat.categoryName.substring(0, 20) + '...' : cat.categoryName,
        safe: cat.safeActs + cat.safeConditions,
        unsafe: cat.unsafeActs + cat.unsafeConditions,
        total: cat.count,
      }));
  }, [statistics]);

  // Risk level data for bar chart
  const riskLevelData = useMemo(() => {
    if (!statistics?.riskLevelDistribution) return [];
    return statistics.riskLevelDistribution.map(risk => ({
      level: risk.riskLevel,
      count: risk.count,
      color: risk.riskLevel === 'Critical' ? '#991b1b' :
        risk.riskLevel === 'High' ? '#dc2626' :
          risk.riskLevel === 'Medium' ? '#f59e0b' : '#22c55e',
    }));
  }, [statistics]);

  // Plant-wise data
  const plantWiseData = useMemo(() => {
    const roundsArray = Array.isArray(rounds) ? rounds : [];
    if (!roundsArray || !plants || roundsArray.length === 0) return [];
    const plantCounts = {};
    roundsArray.forEach(round => {
      const plantId = round.plantId;
      if (plantId) {
        if (!plantCounts[plantId]) {
          plantCounts[plantId] = 0;
        }
        plantCounts[plantId]++;
      }
    });

    return plants
      .filter(plant => plantCounts[plant.plantId])
      .map(plant => ({
        name: plant.plantName?.length > 15 ? plant.plantName.substring(0, 15) + '...' : plant.plantName,
        count: plantCounts[plant.plantId] || 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [rounds, plants]);

  // Pie Chart Component
  const PieChart = ({ data, size = 200 }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return <div className="chart-empty">No data available</div>;

    let currentAngle = -90;
    const radius = size / 2 - 10;
    const center = size / 2;

    const segments = data.map((item, index) => {
      const percentage = (item.value / total) * 100;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle = endAngle;

      const x1 = center + radius * Math.cos((startAngle * Math.PI) / 180);
      const y1 = center + radius * Math.sin((startAngle * Math.PI) / 180);
      const x2 = center + radius * Math.cos((endAngle * Math.PI) / 180);
      const y2 = center + radius * Math.sin((endAngle * Math.PI) / 180);
      const largeArc = angle > 180 ? 1 : 0;

      return (
        <g key={index}>
          <path
            d={`M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
            fill={item.color}
            stroke="var(--bg-card)"
            strokeWidth="2"
          />
        </g>
      );
    });

    return (
      <div className="pie-chart-container">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments}
        </svg>
        <div className="pie-legend">
          {data.map((item, index) => (
            <div key={index} className="legend-item">
              <span className="legend-color" style={{ backgroundColor: item.color }}></span>
              <span className="legend-label">{item.name}</span>
              <span className="legend-value">{item.value} ({(item.value / total * 100).toFixed(1)}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Bar Chart Component
  const BarChart = ({ data, maxValue, height = 300, showSafeUnsafe = false }) => {
    if (!data || data.length === 0) return <div className="chart-empty">No data available</div>;

    const max = maxValue || Math.max(...data.map(d => showSafeUnsafe ? d.safe + d.unsafe : d.count || d.total));
    const barWidth = (100 / data.length) - 2;

    return (
      <div className="bar-chart-container" style={{ height: `${height}px` }}>
        <div className="bar-chart-bars">
          {data.map((item, index) => {
            const safeHeight = showSafeUnsafe ? ((item.safe / max) * 100) : 0;
            const unsafeHeight = showSafeUnsafe ? ((item.unsafe / max) * 100) : 0;
            const totalHeight = showSafeUnsafe ? safeHeight + unsafeHeight : ((item.count || item.total) / max) * 100;

            return (
              <div key={index} className="bar-group" style={{ width: `${barWidth}%` }}>
                {showSafeUnsafe ? (
                  <>
                    <div className="bar-stack">
                      <div
                        className="bar bar-safe"
                        style={{ height: `${safeHeight}%` }}
                        title={`Safe: ${item.safe}`}
                      ></div>
                      <div
                        className="bar bar-unsafe"
                        style={{ height: `${unsafeHeight}%` }}
                        title={`Unsafe: ${item.unsafe}`}
                      ></div>
                    </div>
                    <div className="bar-label">{item.name}</div>
                    <div className="bar-value">{item.total}</div>
                  </>
                ) : (
                  <>
                    <div
                      className="bar bar-single"
                      style={{
                        height: `${totalHeight}%`,
                        backgroundColor: item.color || 'var(--accent-primary)'
                      }}
                      title={`${item.level || item.name}: ${item.count || item.total}`}
                    ></div>
                    <div className="bar-label">{item.level || item.name}</div>
                    <div className="bar-value">{item.count || item.total}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Line Chart Component
  const LineChart = ({ data, height = 300 }) => {
    if (!data || data.length === 0) return <div className="chart-empty">No data available</div>;

    const maxValue = Math.max(...data.map(d => d.total || 0), 1);
    const chartWidth = 100;
    const chartHeight = 100;
    const padding = 10;

    const points = data.map((item, index) => {
      const x = padding + (index / (data.length - 1 || 1)) * (chartWidth - 2 * padding);
      const y = chartHeight - padding - ((item.total || 0) / maxValue) * (chartHeight - 2 * padding);
      return { x, y, value: item.total || 0, date: item.date };
    });

    const pathData = points.map((point, index) =>
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ');

    return (
      <div className="line-chart-container" style={{ height: `${height}px` }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="line-chart-svg">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`${pathData} L ${points[points.length - 1].x} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`}
            fill="url(#lineGradient)"
            className="line-area"
          />
          <path
            d={pathData}
            fill="none"
            stroke="var(--accent-primary)"
            strokeWidth="0.5"
            className="line-path"
          />
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="1"
              fill="var(--accent-primary)"
              className="line-point"
            />
          ))}
        </svg>
        <div className="line-chart-labels">
          {data.map((item, index) => {
            const labelPosition = (index / (data.length - 1 || 1)) * 100;
            return (
              <div
                key={index}
                className="line-label"
                style={{ left: `${labelPosition}%` }}
              >
                {item.date}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Check for cached plants - if we have them, show page immediately
  const hasCachedPlants = sessionStorage.getItem('plants_data');

  if (
    loading &&
    !hasCachedPlants &&
    !trends &&
    !statistics &&
    !hasCachedPlantsOnMount
  ) {
    return (
      <div className="pagecontainer safety-observation-dashboard">
        <PageLoading message="Loading dashboard..." />
      </div>
    );
  }

  if (!canView) {
    if (hasCachedPlants || hasCachedPlantsOnMount) {
      return (
        <div className="pagecontainer safety-observation-dashboard">
          <div className="dashboard-header">
            <div>
              <h1>
                <HiOutlineShieldCheck className="dashboard-icon" />
                Safety Observation Analytics
              </h1>
              <p className="dashboard-subtitle">
                Comprehensive safety performance metrics and insights
              </p>
            </div>
          </div>
          <div className="error-message">
            You do not have permission to view Safety Observation analytics
          </div>
        </div>
      );
    }
    return (
      <div className="pagecontainer safety-observation-dashboard">
        <div className="error-message">
          You do not have permission to view Safety Observation analytics
        </div>
      </div>
    );
  }

  return (
    <div className="pagecontainer safety-observation-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>
            <HiOutlineShieldCheck className="dashboard-icon" />
            Safety Observation Analytics
          </h1>
          <p className="dashboard-subtitle">
            Comprehensive safety performance metrics and insights
          </p>
        </div>
        <div className="header-actions">
          <div className="filters-row">
            <div className="date-range-selector">
              <label htmlFor="start-date">From:</label>
              <input
                id="start-date"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              />
              <label htmlFor="end-date">To:</label>
              <input
                id="end-date"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              />
            </div>
            <div className="plant-filter">
              <label htmlFor="plant-select">Plant:</label>
              <select
                id="plant-select"
                value={selectedPlantId}
                onChange={handlePlantChange}
                disabled={!hasCachedPlants && loading}
              >
                <option value="">All Plants</option>
                {plants && plants.length > 0 ? (
                  plants.map((plant) => (
                    <option key={plant.plantId} value={plant.plantId}>
                      {plant.plantName}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading plants...</option>
                )}
              </select>
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={() => navigate('/home/safety-observation-entry')}
            type="button"
          >
            <HiOutlineShieldCheck /> New Observation
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Show loading indicator only when dashboard data is loading, but keep UI visible */}
      {loading && !trends && !statistics && (
        <div className="dashboard-loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      )}

      {trends && statistics && metrics ? (
        <>
          {/* Key Metrics Cards - Standardized UI */}
          <div 
            style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", 
              gap: "16px", 
              marginBottom: "24px" 
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                borderLeft: `4px solid ${SEMANTIC_SUCCESS_MID}`,
                padding: "18px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                cursor: 'pointer'
              }}
              onClick={() => navigate('/home/safety-observation/reports', { state: { activeTab: 'safe' } })}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <div style={{ padding: "6px", background: `${SEMANTIC_SUCCESS_MID}18`, borderRadius: "6px", color: SEMANTIC_SUCCESS_MID }}>
                  <HiOutlineCheckCircle size={18} />
                </div>
                <p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Safe Observations
                </p>
              </div>
              <h2 style={{ margin: "0 0 4px 0", fontSize: "26px", fontWeight: 700, color: "#065f46" }}>
                {metrics.totalSafe}
              </h2>
              <p style={{ margin: 0, fontSize: "11px", color: "#22c55e", fontWeight: 600 }}>{metrics.safetyRatio}% Ratio</p>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                borderLeft: `4px solid #ef4444`,
                padding: "18px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                cursor: 'pointer'
              }}
              onClick={() => navigate('/home/safety-observation/reports', { state: { activeTab: 'unsafe' } })}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <div style={{ padding: "6px", background: `#ef444418`, borderRadius: "6px", color: "#ef4444" }}>
                  <HiOutlineExclamationTriangle size={18} />
                </div>
                <p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Unsafe Observations
                </p>
              </div>
              <h2 style={{ margin: "0 0 4px 0", fontSize: "26px", fontWeight: 700, color: "#991b1b" }}>
                {metrics.totalUnsafe}
              </h2>
              <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>Requires Attention</p>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                borderLeft: `4px solid #f59e0b`,
                padding: "18px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                cursor: 'pointer'
              }}
              onClick={() => navigate('/home/safety-observation/reports', { state: { activeTab: 'action' } })}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <div style={{ padding: "6px", background: `#f59e0b18`, borderRadius: "6px", color: "#f59e0b" }}>
                  <HiOutlineClock size={18} />
                </div>
                <p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Open Items
                </p>
              </div>
              <h2 style={{ margin: "0 0 4px 0", fontSize: "26px", fontWeight: 700, color: "#92400e" }}>
                {trends.openUnsafeItems}
              </h2>
              <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>Pending Actions</p>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                borderLeft: `4px solid #3b82f6`,
                padding: "18px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                cursor: 'pointer'
              }}
              onClick={() => navigate('/home/safety-observation/reports', { state: { activeTab: 'action' } })}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <div style={{ padding: "6px", background: `#3b82f618`, borderRadius: "6px", color: "#3b82f6" }}>
                  <HiOutlineCheckCircle size={18} />
                </div>
                <p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Closed Items
                </p>
              </div>
              <h2 style={{ margin: "0 0 4px 0", fontSize: "26px", fontWeight: 700, color: "#1e3a8a" }}>
                {trends.closedUnsafeItems}
              </h2>
              <p style={{ margin: 0, fontSize: "11px", color: SEMANTIC_SUCCESS_MID, fontWeight: 600 }}>{metrics.closureRate}% Closure</p>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                borderLeft: `4px solid #6366f1`,
                padding: "18px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <div style={{ padding: "6px", background: `#6366f118`, borderRadius: "6px", color: "#6366f1" }}>
                  <HiOutlineChartBar size={18} />
                </div>
                <p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Total Rounds
                </p>
              </div>
              <h2 style={{ margin: "0 0 4px 0", fontSize: "26px", fontWeight: 700, color: "#3730a3" }}>
                {trends.totalRounds}
              </h2>
              <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>Sessions Conducted</p>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            {/* Pie Chart - Observation Type Distribution */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Observation Type Distribution</h3>
                <span className="chart-subtitle">Breakdown by Type</span>
              </div>
              <div className="chart-body">
                <PieChart data={observationTypeData} size={220} />
              </div>
            </div>

            {/* Bar Chart - Category-wise Breakdown */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Top Categories Analysis</h3>
                <span className="chart-subtitle">Safe vs Unsafe by Category</span>
              </div>
              <div className="chart-body">
                <BarChart
                  data={categoryData}
                  height={280}
                  showSafeUnsafe={true}
                />
                <div className="chart-legend">
                  <div className="legend-item-inline">
                    <span className="legend-dot safe"></span>
                    <span>Safe</span>
                  </div>
                  <div className="legend-item-inline">
                    <span className="legend-dot unsafe"></span>
                    <span>Unsafe</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Line Chart - Trends Over Time */}
            <div className="chart-card chart-card-wide">
              <div className="chart-header">
                <h3>Observation Trends</h3>
                <span className="chart-subtitle">Daily Observation Rounds (Last 14 Days)</span>
              </div>
              <div className="chart-body">
                <LineChart data={timeSeriesData} height={250} />
              </div>
            </div>

            {/* Bar Chart - Risk Level Distribution */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Risk Level Distribution</h3>
                <span className="chart-subtitle">Unsafe Items by Risk Level</span>
              </div>
              <div className="chart-body">
                <BarChart
                  data={riskLevelData}
                  height={280}
                  maxValue={Math.max(...riskLevelData.map(r => r.count), 1)}
                />
              </div>
            </div>

            {/* Bar Chart - Plant-wise Comparison */}
            {plantWiseData.length > 0 && (
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Plant-wise Performance</h3>
                  <span className="chart-subtitle">Observation Rounds by Plant</span>
                </div>
                <div className="chart-body">
                  <BarChart
                    data={plantWiseData.map(p => ({ name: p.name, count: p.count }))}
                    height={280}
                  />
                </div>
              </div>
            )}

            {/* Safety Score Trend */}
            {statistics.averageSafetyScore > 0 && (
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Safety Score Analysis</h3>
                  <span className="chart-subtitle">Average Performance Score</span>
                </div>
                <div className="chart-body">
                  <div className="score-display">
                    <div className="score-circle" style={{
                      background: `conic-gradient(
                        #22c55e 0deg ${(statistics.averageSafetyScore / 100) * 360}deg,
                        var(--border-primary) ${(statistics.averageSafetyScore / 100) * 360}deg 360deg
                      )`
                    }}>
                      <div className="score-inner">
                        <div className="score-number">{statistics.averageSafetyScore.toFixed(1)}</div>
                        <div className="score-label">Score</div>
                      </div>
                    </div>
                    <div className="score-breakdown">
                      <div className="score-item">
                        <span className="score-indicator excellent"></span>
                        <span>Excellent (85-100)</span>
                      </div>
                      <div className="score-item">
                        <span className="score-indicator good"></span>
                        <span>Good (70-84)</span>
                      </div>
                      <div className="score-item">
                        <span className="score-indicator fair"></span>
                        <span>Fair (50-69)</span>
                      </div>
                      <div className="score-item">
                        <span className="score-indicator poor"></span>
                        <span>Needs Improvement (&lt;50)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Detailed Statistics Table */}
          {statistics.categoryWiseBreakdown && statistics.categoryWiseBreakdown.length > 0 && (
            <div className="dashboard-section">
              <div className="section-header">
                <h3>Category-wise Detailed Analysis</h3>
                <span className="section-subtitle">Comprehensive breakdown by safety category</span>
              </div>
              <div className="category-table-container">
                <table className="category-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Total</th>
                      <th>Safe Acts</th>
                      <th>Safe Conditions</th>
                      <th>Unsafe Acts</th>
                      <th>Unsafe Conditions</th>
                      <th>Safety Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.categoryWiseBreakdown
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 10)
                      .map((category, index) => {
                        const totalSafe = category.safeActs + category.safeConditions;
                        const totalUnsafe = category.unsafeActs + category.unsafeConditions;
                        const total = category.count;
                        const ratio = total > 0 ? ((totalSafe / total) * 100).toFixed(1) : 0;

                        return (
                          <tr key={index}>
                            <td className="category-name-cell">{category.categoryName || `Category #${category.categoryId}`}</td>
                            <td><strong>{total}</strong></td>
                            <td className="safe-cell">{category.safeActs}</td>
                            <td className="safe-cell">{category.safeConditions}</td>
                            <td className="unsafe-cell">{category.unsafeActs}</td>
                            <td className="unsafe-cell">{category.unsafeConditions}</td>
                            <td>
                              <div className="ratio-bar-container">
                                <div
                                  className="ratio-bar"
                                  style={{ width: `${ratio}%` }}
                                ></div>
                                <span className="ratio-text">{ratio}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="dashboard-empty-state">
          <div className="empty-state-content">
            <HiOutlineChartBar className="empty-icon" />
            <h3>No Data Available</h3>
            <p>
              {loading
                ? 'Loading dashboard data...'
                : 'No safety observation data found for the selected date range and filters. Try adjusting your filters or create a new observation.'}
            </p>
            {!loading && (
              <button
                className="btn-primary"
                onClick={() => navigate('/home/safety-observation-entry')}
              >
                <HiOutlineShieldCheck /> Create First Observation
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SafetyObservationDashboard;
